import React, { useState, useEffect, useRef } from 'react';
import { Camera, Plus, X, Heart, Calendar, Sparkles, Book, Music, Target, Sun, Star, Grid, List, Image as ImageIcon, MessageCircle, ThumbsUp, Filter, Send, AlertCircle } from 'lucide-react';
import { generateFingerprint, checkAlreadyEntered, markAsEntered } from './fingerprint';
import WelcomeScreen from './WelcomeScreen';
import MusicPlayer from './MusicPlayer';

// PERSONALISIERUNG - HIER DEINEN NAMEN EINTRAGEN!
const OWNER_NAME = 'Jonathan';  // ← Ändere dies zu deinem Namen
const YEAR = '2025';         // ← Aktuelles Jahr

const API_BASE_URL = '/api';

export default function DigitalesFreundebuch() {
  // Welcome Screen State
  const [showWelcome, setShowWelcome] = useState(true);
  
  // Core States
  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // View & Filter States
  const [view, setView] = useState('grid');
  const [selectedYear, setSelectedYear] = useState('all');
  
  // Fingerprinting
  const [userFingerprint, setUserFingerprint] = useState(null);
  const [alreadyEnteredWarning, setAlreadyEnteredWarning] = useState(null);
  
  // Likes & Comments
  const [likes, setLikes] = useState({});
  const [comments, setComments] = useState({});
  const [showComments, setShowComments] = useState({});
  const [commentForm, setCommentForm] = useState({});

  const [formData, setFormData] = useState({
    name: '', nickname: '', howWeKnow: '', threeWords: '',
    grateful: '', yearGoal: '', motivation: '', perfectSunday: '',
    appreciation: '', bestMemory: '', wishes: '', quote: '',
    songPodcast: '', bookFilm: '', fiveYears: '',
    photo: null, photoPreview: null
  });

  const fileInputRef = useRef(null);

  useEffect(() => {
    initializeApp();
  }, []);

  useEffect(() => {
    filterEntries();
  }, [entries, selectedYear]);

  const initializeApp = async () => {
    const fp = await generateFingerprint();
    setUserFingerprint(fp);
    await loadEntries();
  };

  const loadEntries = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/entries.php`);
      const data = await response.json();
      if (data.success) setEntries(data.data || []);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterEntries = () => {
    if (selectedYear === 'all') {
      setFilteredEntries(entries);
    } else {
      setFilteredEntries(entries.filter(e => 
        new Date(e.date).getFullYear().toString() === selectedYear
      ));
    }
  };

  const getAvailableYears = () => {
    const years = entries.map(e => new Date(e.date).getFullYear());
    return [...new Set(years)].sort((a, b) => b - a);
  };

  const handleAddEntryClick = async () => {
    const check = await checkAlreadyEntered();
    if (check.alreadyEntered) {
      setAlreadyEnteredWarning({
        date: new Date(check.date).toLocaleDateString('de-DE'),
        method: check.method
      });
    }
    setShowForm(true);
    setShowWelcome(false); // Schließe Welcome Screen
  };

  const handleViewEntries = () => {
    setShowWelcome(false); // Schließe Welcome Screen
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxSize = 800;
          let width = img.width, height = img.height;
          if (width > height && width > maxSize) {
            height *= maxSize / width; width = maxSize;
          } else if (height > maxSize) {
            width *= maxSize / height; height = maxSize;
          }
          canvas.width = width; canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          const compressed = canvas.toDataURL('image/webp', 0.8);
          setFormData(prev => ({...prev, photo: compressed, photoPreview: compressed}));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const saveEntry = async () => {
    if (!formData.name || !formData.howWeKnow || !formData.threeWords) {
      alert('Bitte fülle alle Pflichtfelder aus');
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/entries.php`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(formData)
      });
      const result = await response.json();
      if (result.success) {
        await markAsEntered();
        await loadEntries();
        setShowForm(false);
        resetForm();
        alert('Eintrag erfolgreich gespeichert! 🎉');
      } else {
        alert(result.error || 'Fehler beim Speichern');
      }
    } catch (error) {
      alert('Verbindungsfehler');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', nickname: '', howWeKnow: '', threeWords: '',
      grateful: '', yearGoal: '', motivation: '', perfectSunday: '',
      appreciation: '', bestMemory: '', wishes: '', quote: '',
      songPodcast: '', bookFilm: '', fiveYears: '',
      photo: null, photoPreview: null
    });
    setAlreadyEnteredWarning(null);
  };

  // Like Functions
  const toggleLike = async (entryId) => {
    if (!userFingerprint) return;
    const userName = prompt('Dein Name für das Like:');
    if (!userName?.trim()) return;
    try {
      const response = await fetch(`${API_BASE_URL}/like.php`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({entry_id: entryId, user_name: userName.trim(), fingerprint: userFingerprint})
      });
      const result = await response.json();
      if (result.success) {
        await loadLikes(entryId);
        await loadEntries();
      }
    } catch (error) {
      console.error('Like error:', error);
    }
  };

  const loadLikes = async (entryId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/like.php?entry_id=${entryId}`);
      const result = await response.json();
      if (result.success) {
        setLikes(prev => ({...prev, [entryId]: result.likes}));
      }
    } catch (error) {
      console.error('Load likes error:', error);
    }
  };

  const formatLikes = (entryId) => {
    const entryLikes = likes[entryId] || [];
    if (entryLikes.length === 0) return null;
    const names = entryLikes.map(l => l.user_name);
    if (names.length <= 3) return names.join(', ');
    return `${names.slice(0,2).join(', ')} und ${names.length - 2} weitere`;
  };

  // Comment Functions
  const toggleComments = async (entryId) => {
    if (!showComments[entryId]) await loadComments(entryId);
    setShowComments(prev => ({...prev, [entryId]: !prev[entryId]}));
  };

  const loadComments = async (entryId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/comment.php?entry_id=${entryId}`);
      const result = await response.json();
      if (result.success) {
        setComments(prev => ({...prev, [entryId]: result.comments}));
      }
    } catch (error) {
      console.error('Load comments error:', error);
    }
  };

  const submitComment = async (entryId) => {
    const form = commentForm[entryId];
    if (!form?.name || !form?.text) {
      alert('Bitte Name und Kommentar ausfüllen');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/comment.php`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          entry_id: entryId,
          user_name: form.name.trim(),
          comment_text: form.text.trim(),
          fingerprint: userFingerprint
        })
      });
      const result = await response.json();
      if (result.success) {
        setCommentForm(prev => ({...prev, [entryId]: {name: '', text: ''}}));
        await loadComments(entryId);
        await loadEntries();
      }
    } catch (error) {
      console.error('Comment error:', error);
    }
  };

  // Show Welcome Screen
  if (showWelcome) {
    return (
      <WelcomeScreen 
        ownerName={OWNER_NAME}
        year={YEAR}
        onViewEntries={handleViewEntries}
        onCreateEntry={handleAddEntryClick}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50 flex items-center justify-center">
        <Sparkles className="w-12 h-12 text-rose-400 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Book className="w-8 h-8 text-rose-500" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Mein Freundebuch</h1>
                <p className="text-sm text-gray-500">{filteredEntries.length} {filteredEntries.length === 1 ? 'Eintrag' : 'Einträge'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {entries.length > 0 && (
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:border-rose-500 focus:ring-0"
                  >
                    <option value="all">Alle Jahre</option>
                    {getAvailableYears().map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button onClick={() => setView('grid')} className={`p-2 rounded ${view === 'grid' ? 'bg-white shadow-sm' : ''}`} title="Grid">
                  <Grid className="w-5 h-5" />
                </button>
                <button onClick={() => setView('timeline')} className={`p-2 rounded ${view === 'timeline' ? 'bg-white shadow-sm' : ''}`} title="Timeline">
                  <List className="w-5 h-5" />
                </button>
                <button onClick={() => setView('collage')} className={`p-2 rounded ${view === 'collage' ? 'bg-white shadow-sm' : ''}`} title="Fotos">
                  <ImageIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {filteredEntries.length === 0 ? (
          <div className="text-center py-16">
            <Heart className="w-16 h-16 text-rose-300 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">
              {selectedYear === 'all' ? 'Noch keine Einträge' : `Keine Einträge aus ${selectedYear}`}
            </h2>
            <p className="text-gray-600">
              {selectedYear === 'all' ? 'Sei der Erste und trage dich ein!' : 'Wähle ein anderes Jahr'}
            </p>
          </div>
        ) : view === 'grid' ? (
          <GridView 
            entries={filteredEntries}
            setSelectedEntry={setSelectedEntry}
            toggleLike={toggleLike}
            formatLikes={formatLikes}
            toggleComments={toggleComments}
            loadLikes={loadLikes}
          />
        ) : view === 'timeline' ? (
          <TimelineView 
            entries={filteredEntries}
            setSelectedEntry={setSelectedEntry}
            toggleLike={toggleLike}
            formatLikes={formatLikes}
            loadLikes={loadLikes}
          />
        ) : (
          <CollageView entries={filteredEntries} setSelectedEntry={setSelectedEntry} />
        )}
      </main>

      {/* Floating Add Button */}
      <button
        onClick={handleAddEntryClick}
        className="fixed bottom-8 right-8 w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-600 text-white rounded-full shadow-2xl hover:scale-110 transition-transform duration-200 flex items-center justify-center z-50"
      >
        <Plus className="w-8 h-8" />
      </button>

      {/* Entry Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 overflow-y-auto">
          <div className="min-h-screen px-4 py-8">
            <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-2xl">
              <div className="sticky top-0 bg-white rounded-t-3xl border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-2xl font-bold text-gray-900">Neuer Eintrag</h2>
                <button onClick={() => { setShowForm(false); resetForm(); }} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>

              {alreadyEnteredWarning && (
                <div className="mx-6 mt-6 bg-amber-50 border-2 border-amber-200 rounded-xl p-4 flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-amber-900 mb-1">Du hast bereits einen Eintrag erstellt</h4>
                    <p className="text-sm text-amber-700">
                      Am {alreadyEnteredWarning.date} wurde ein Eintrag von diesem Gerät erstellt.
                      Du kannst trotzdem fortfahren, aber normalerweise ist nur ein Eintrag pro Person vorgesehen.
                    </p>
                  </div>
                </div>
              )}

              <div className="p-6 space-y-8">
                {/* Photo Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Foto <span className="text-gray-400">(optional)</span>
                  </label>
                  {formData.photoPreview ? (
                    <div className="relative">
                      <img src={formData.photoPreview} alt="Vorschau" className="w-full h-64 object-cover rounded-2xl" />
                      <button
                        onClick={() => setFormData(prev => ({ ...prev, photo: null, photoPreview: null }))}
                        className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-gray-700 px-4 py-2 rounded-full shadow-lg hover:bg-white"
                      >
                        Entfernen
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full border-2 border-dashed border-gray-300 rounded-2xl p-8 hover:border-rose-300 hover:bg-rose-50 transition-all group"
                    >
                      <Camera className="w-12 h-12 text-gray-400 group-hover:text-rose-500 mx-auto mb-3" />
                      <p className="text-sm font-medium text-gray-600 group-hover:text-rose-600 mb-1">
                        Foto aufnehmen oder hochladen
                      </p>
                      <p className="text-xs text-gray-400">Auf Mobilgeräten: Kamera oder Galerie wählbar</p>
                    </button>
                  )}
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                </div>

                {/* Basisdaten */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Star className="w-5 h-5 text-rose-500" />
                    Basisdaten
                  </h3>
                  <input type="text" required value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 focus:ring-0" placeholder="Dein Name *" />
                  <input type="text" value={formData.nickname} onChange={(e) => setFormData(prev => ({ ...prev, nickname: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 focus:ring-0" placeholder="Spitzname" />
                  <input type="text" required value={formData.howWeKnow} onChange={(e) => setFormData(prev => ({ ...prev, howWeKnow: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 focus:ring-0" placeholder="So kennen wir uns *" />
                  <input type="text" required value={formData.threeWords} onChange={(e) => setFormData(prev => ({ ...prev, threeWords: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 focus:ring-0" placeholder="Drei Worte über mich *" />
                </div>

                {/* Persönliches */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Heart className="w-5 h-5 text-rose-500" />
                    Persönliches
                  </h3>
                  <textarea value={formData.grateful} onChange={(e) => setFormData(prev => ({ ...prev, grateful: e.target.value }))} rows={2}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 focus:ring-0 resize-none" placeholder="Wofür ich aktuell dankbar bin" />
                  <input type="text" value={formData.yearGoal} onChange={(e) => setFormData(prev => ({ ...prev, yearGoal: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 focus:ring-0" placeholder="Mein Ziel für dieses Jahr" />
                  <input type="text" value={formData.motivation} onChange={(e) => setFormData(prev => ({ ...prev, motivation: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 focus:ring-0" placeholder="Das motiviert mich" />
                  <textarea value={formData.perfectSunday} onChange={(e) => setFormData(prev => ({ ...prev, perfectSunday: e.target.value }))} rows={2}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 focus:ring-0 resize-none" placeholder="Mein perfekter Sonntag" />
                </div>

                {/* Freundschaft */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-rose-500" />
                    Freundschaft
                  </h3>
                  <textarea value={formData.appreciation} onChange={(e) => setFormData(prev => ({ ...prev, appreciation: e.target.value }))} rows={2}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 focus:ring-0 resize-none" placeholder="Das schätze ich an dir" />
                  <textarea value={formData.bestMemory} onChange={(e) => setFormData(prev => ({ ...prev, bestMemory: e.target.value }))} rows={2}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 focus:ring-0 resize-none" placeholder="Unsere beste gemeinsame Erinnerung" />
                  <textarea value={formData.wishes} onChange={(e) => setFormData(prev => ({ ...prev, wishes: e.target.value }))} rows={2}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 focus:ring-0 resize-none" placeholder="Was ich dir wünsche" />
                </div>

                {/* Inspiration */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Music className="w-5 h-5 text-rose-500" />
                    Inspiration
                  </h3>
                  <textarea value={formData.quote} onChange={(e) => setFormData(prev => ({ ...prev, quote: e.target.value }))} rows={2}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 focus:ring-0 resize-none" placeholder="Lieblingszitat" />
                  <input type="text" value={formData.songPodcast} onChange={(e) => setFormData(prev => ({ ...prev, songPodcast: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 focus:ring-0" placeholder="Spotify/YouTube Link oder Text" />
                  <input type="text" value={formData.bookFilm} onChange={(e) => setFormData(prev => ({ ...prev, bookFilm: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 focus:ring-0" placeholder="Buch oder Film, der mich geprägt hat" />
                </div>

                {/* Zukunft */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Target className="w-5 h-5 text-rose-500" />
                    Zukunft
                  </h3>
                  <textarea value={formData.fiveYears} onChange={(e) => setFormData(prev => ({ ...prev, fiveYears: e.target.value }))} rows={3}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 focus:ring-0 resize-none" placeholder="In 5 Jahren sehe ich mich..." />
                </div>

                <div className="pt-4">
                  <button
                    onClick={saveEntry}
                    disabled={!formData.name || !formData.howWeKnow || !formData.threeWords || saving}
                    className="w-full bg-gradient-to-r from-rose-500 to-pink-600 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Wird gespeichert...' : 'Eintrag speichern'}
                  </button>
                  <p className="text-xs text-gray-500 text-center mt-3">Dein Eintrag wird für alle sichtbar sein</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail View Modal */}
      {selectedEntry && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 overflow-y-auto">
          <div className="min-h-screen px-4 py-8">
            <div className="max-w-3xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden">
              <button onClick={() => setSelectedEntry(null)} className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm text-gray-700 p-2 rounded-full shadow-lg hover:bg-white z-10">
                <X className="w-6 h-6" />
              </button>

              {(selectedEntry.photoURL || selectedEntry.photo) && (
                <div className="h-80 overflow-hidden bg-gray-100">
                  <img src={selectedEntry.photoURL || selectedEntry.photo} alt={selectedEntry.name} className="w-full h-full object-cover" />
                </div>
              )}

              <div className="p-8 space-y-8">
                <div className="text-center border-b border-gray-200 pb-6">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">{selectedEntry.name}</h2>
                  {selectedEntry.nickname && <p className="text-lg text-rose-500 mb-3">"{selectedEntry.nickname}"</p>}
                  <div className="flex items-center justify-center text-sm text-gray-500">
                    <Calendar className="w-4 h-4 mr-2" />
                    {new Date(selectedEntry.date).toLocaleDateString('de-DE', {year: 'numeric', month: 'long', day: 'numeric'})}
                  </div>
                </div>

                {/* Basisdaten */}
                <div className="space-y-4">
                  <h3 className="font-bold text-xl text-gray-900 flex items-center gap-2 border-b pb-2">
                    <Star className="w-5 h-5 text-rose-500" />
                    Basisdaten
                  </h3>
                  {selectedEntry.howWeKnow && <div><h4 className="font-semibold text-gray-900 mb-2">So kennen wir uns</h4><p className="text-gray-700">{selectedEntry.howWeKnow}</p></div>}
                  {selectedEntry.threeWords && <div><h4 className="font-semibold text-gray-900 mb-2">Drei Worte über mich</h4><p className="text-gray-700 italic">"{selectedEntry.threeWords}"</p></div>}
                </div>

                {/* Persönliches */}
                {(selectedEntry.grateful || selectedEntry.yearGoal || selectedEntry.motivation || selectedEntry.perfectSunday) && (
                  <div className="space-y-4">
                    <h3 className="font-bold text-xl text-gray-900 flex items-center gap-2 border-b pb-2">
                      <Heart className="w-5 h-5 text-rose-500" />
                      Persönliches
                    </h3>
                    {selectedEntry.grateful && <div><h4 className="font-semibold text-gray-900 mb-2">Dankbar für</h4><p className="text-gray-700">{selectedEntry.grateful}</p></div>}
                    {selectedEntry.yearGoal && <div><h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2"><Target className="w-4 h-4 text-rose-500" />Ziel für dieses Jahr</h4><p className="text-gray-700">{selectedEntry.yearGoal}</p></div>}
                    {selectedEntry.motivation && <div><h4 className="font-semibold text-gray-900 mb-2">Motivation</h4><p className="text-gray-700">{selectedEntry.motivation}</p></div>}
                    {selectedEntry.perfectSunday && <div><h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2"><Sun className="w-4 h-4 text-amber-500" />Perfekter Sonntag</h4><p className="text-gray-700">{selectedEntry.perfectSunday}</p></div>}
                  </div>
                )}

                {/* Freundschaft */}
                {(selectedEntry.appreciation || selectedEntry.bestMemory || selectedEntry.wishes) && (
                  <div className="space-y-4">
                    <h3 className="font-bold text-xl text-gray-900 flex items-center gap-2 border-b pb-2">
                      <Sparkles className="w-5 h-5 text-rose-500" />
                      Freundschaft
                    </h3>
                    {selectedEntry.appreciation && <div className="bg-rose-50 rounded-2xl p-6"><h4 className="font-semibold text-gray-900 mb-2">Das schätze ich an dir</h4><p className="text-gray-700">{selectedEntry.appreciation}</p></div>}
                    {selectedEntry.bestMemory && <div><h4 className="font-semibold text-gray-900 mb-2">Beste gemeinsame Erinnerung</h4><p className="text-gray-700">{selectedEntry.bestMemory}</p></div>}
                    {selectedEntry.wishes && <div><h4 className="font-semibold text-gray-900 mb-2">Wünsche für dich</h4><p className="text-gray-700">{selectedEntry.wishes}</p></div>}
                  </div>
                )}

                {/* Inspiration */}
                {(selectedEntry.quote || selectedEntry.songPodcast || selectedEntry.bookFilm) && (
                  <div className="space-y-4">
                    <h3 className="font-bold text-xl text-gray-900 flex items-center gap-2 border-b pb-2">
                      <Music className="w-5 h-5 text-rose-500" />
                      Inspiration
                    </h3>
                    {selectedEntry.quote && <div className="border-l-4 border-rose-500 pl-6 py-2"><h4 className="font-semibold text-gray-900 mb-2">Lieblingszitat</h4><p className="text-gray-700 italic">"{selectedEntry.quote}"</p></div>}
                    {selectedEntry.songPodcast && (
                      <MusicPlayer url={selectedEntry.songPodcast} title="Lieblingssong / Podcast" />
                    )}
                    {selectedEntry.bookFilm && <div><h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2"><Book className="w-4 h-4 text-rose-500" />Buch oder Film</h4><p className="text-gray-700">{selectedEntry.bookFilm}</p></div>}
                  </div>
                )}

                {/* Zukunft */}
                {selectedEntry.fiveYears && (
                  <div className="space-y-4">
                    <h3 className="font-bold text-xl text-gray-900 flex items-center gap-2 border-b pb-2">
                      <Target className="w-5 h-5 text-rose-500" />
                      Zukunft
                    </h3>
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6">
                      <h4 className="font-semibold text-gray-900 mb-2">In 5 Jahren...</h4>
                      <p className="text-gray-700">{selectedEntry.fiveYears}</p>
                    </div>
                  </div>
                )}

                {/* Likes & Comments Section */}
                <div className="border-t pt-6 space-y-6">
                  {/* Likes */}
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <button
                      onClick={() => { toggleLike(selectedEntry.id); loadLikes(selectedEntry.id); }}
                      className="flex items-center gap-2 px-4 py-2 rounded-full border-2 border-gray-200 hover:border-rose-500 hover:bg-rose-50 transition-all"
                    >
                      <ThumbsUp className="w-5 h-5" />
                      <span className="font-medium">{selectedEntry.likes_count || 0}</span>
                    </button>
                    {formatLikes(selectedEntry.id) && (
                      <p className="text-sm text-gray-600">{formatLikes(selectedEntry.id)} {selectedEntry.likes_count === 1 ? 'gefällt das' : 'gefällt das'}</p>
                    )}
                  </div>

                  {/* Comments Toggle */}
                  <button
                    onClick={() => toggleComments(selectedEntry.id)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-5 h-5 text-gray-600" />
                      <span className="font-medium text-gray-900">Kommentare ({selectedEntry.comments_count || 0})</span>
                    </div>
                    <span className="text-gray-400">{showComments[selectedEntry.id] ? '▲' : '▼'}</span>
                  </button>

                  {/* Comments Section */}
                  {showComments[selectedEntry.id] && (
                    <div className="space-y-4">
                      {/* Add Comment Form */}
                      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                        <input
                          type="text"
                          placeholder="Dein Name"
                          value={commentForm[selectedEntry.id]?.name || ''}
                          onChange={(e) => setCommentForm(prev => ({...prev, [selectedEntry.id]: {...(prev[selectedEntry.id] || {}), name: e.target.value}}))}
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-rose-500 focus:ring-0"
                        />
                        <textarea
                          placeholder="Schreibe einen Kommentar..."
                          value={commentForm[selectedEntry.id]?.text || ''}
                          onChange={(e) => setCommentForm(prev => ({...prev, [selectedEntry.id]: {...(prev[selectedEntry.id] || {}), text: e.target.value}}))}
                          rows={3}
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-rose-500 focus:ring-0 resize-none"
                        />
                        <button
                          onClick={() => submitComment(selectedEntry.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors"
                        >
                          <Send className="w-4 h-4" />
                          Kommentieren
                        </button>
                      </div>

                      {/* Comments List */}
                      <div className="space-y-3">
                        {(comments[selectedEntry.id] || []).map(comment => (
                          <div key={comment.id} className="bg-white border-2 border-gray-100 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-semibold text-gray-900">{comment.user_name}</span>
                              <span className="text-xs text-gray-400">
                                {new Date(comment.created_at).toLocaleDateString('de-DE')}
                              </span>
                            </div>
                            <p className="text-gray-700">{comment.comment_text}</p>
                          </div>
                        ))}
                        {(!comments[selectedEntry.id] || comments[selectedEntry.id].length === 0) && (
                          <p className="text-center text-gray-400 py-4">Noch keine Kommentare</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Grid View Component
function GridView({ entries, setSelectedEntry, toggleLike, formatLikes, toggleComments, loadLikes }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {entries.map((entry, index) => (
        <div key={entry.id} className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden" style={{animationDelay: `${index * 50}ms`}}>
          {(entry.photoURL || entry.photo) && (
            <div className="aspect-square overflow-hidden bg-gray-100 cursor-pointer" onClick={() => { setSelectedEntry(entry); loadLikes(entry.id); }}>
              <img src={entry.photoURL || entry.photo} alt={entry.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
            </div>
          )}
          <div className="p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-1 cursor-pointer hover:text-rose-500" onClick={() => { setSelectedEntry(entry); loadLikes(entry.id); }}>
              {entry.name}
            </h3>
            {entry.nickname && <p className="text-sm text-rose-500 mb-3">"{entry.nickname}"</p>}
            <p className="text-sm text-gray-600 line-clamp-2 mb-3">{entry.threeWords}</p>
            
            <div className="flex items-center justify-between text-sm border-t pt-3 mt-3">
              <button onClick={() => { toggleLike(entry.id); loadLikes(entry.id); }} className="flex items-center gap-1 text-gray-600 hover:text-rose-500">
                <ThumbsUp className="w-4 h-4" />
                <span>{entry.likes_count || 0}</span>
              </button>
              <button onClick={() => { setSelectedEntry(entry); toggleComments(entry.id); }} className="flex items-center gap-1 text-gray-600 hover:text-rose-500">
                <MessageCircle className="w-4 h-4" />
                <span>{entry.comments_count || 0}</span>
              </button>
              <div className="flex items-center text-xs text-gray-400">
                <Calendar className="w-3 h-3 mr-1" />
                {new Date(entry.date).toLocaleDateString('de-DE')}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Timeline View Component
function TimelineView({ entries, setSelectedEntry, toggleLike, formatLikes, loadLikes }) {
  return (
    <div className="max-w-3xl mx-auto">
      {entries.map((entry, index) => (
        <div key={entry.id} className="relative pl-8 pb-12 border-l-2 border-rose-200" style={{animationDelay: `${index * 50}ms`}}>
          <div className="absolute left-0 top-0 w-4 h-4 bg-rose-500 rounded-full -translate-x-[9px]"></div>
          <div className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden">
            <div className="flex flex-col md:flex-row">
              {(entry.photoURL || entry.photo) && (
                <div className="md:w-48 h-48 overflow-hidden bg-gray-100 flex-shrink-0 cursor-pointer" onClick={() => { setSelectedEntry(entry); loadLikes(entry.id); }}>
                  <img src={entry.photoURL || entry.photo} alt={entry.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                </div>
              )}
              <div className="p-6 flex-1">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xl font-semibold text-gray-900 cursor-pointer hover:text-rose-500" onClick={() => { setSelectedEntry(entry); loadLikes(entry.id); }}>
                    {entry.name}
                  </h3>
                  <span className="text-sm text-gray-400">{new Date(entry.date).toLocaleDateString('de-DE')}</span>
                </div>
                {entry.nickname && <p className="text-sm text-rose-500 mb-2">"{entry.nickname}"</p>}
                <p className="text-gray-600 mb-4">{entry.threeWords}</p>
                {entry.howWeKnow && <p className="text-sm text-gray-500 mb-4"><span className="font-semibold">So kennen wir uns:</span> {entry.howWeKnow}</p>}
                
                <div className="flex items-center gap-4 border-t pt-3">
                  <button onClick={() => { toggleLike(entry.id); loadLikes(entry.id); }} className="flex items-center gap-2 text-gray-600 hover:text-rose-500">
                    <ThumbsUp className="w-4 h-4" />
                    <span className="text-sm">{entry.likes_count || 0}</span>
                  </button>
                  <button onClick={() => setSelectedEntry(entry)} className="flex items-center gap-2 text-gray-600 hover:text-rose-500">
                    <MessageCircle className="w-4 h-4" />
                    <span className="text-sm">{entry.comments_count || 0}</span>
                  </button>
                  <button onClick={() => { setSelectedEntry(entry); loadLikes(entry.id); }} className="text-sm text-rose-500 hover:text-rose-600 font-medium ml-auto">
                    Mehr lesen →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Collage View Component
function CollageView({ entries, setSelectedEntry }) {
  const entriesWithPhotos = entries.filter(e => e.photoURL || e.photo);
  
  if (entriesWithPhotos.length === 0) {
    return (
      <div className="text-center py-16">
        <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">Noch keine Einträge mit Fotos</p>
      </div>
    );
  }

  return (
    <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-4">
      {entriesWithPhotos.map((entry, index) => (
        <div
          key={entry.id}
          className="break-inside-avoid mb-4 group cursor-pointer"
          onClick={() => setSelectedEntry(entry)}
          style={{animationDelay: `${index * 30}ms`}}
        >
          <div className="relative overflow-hidden rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300">
            <img
              src={entry.photoURL || entry.photo}
              alt={entry.name}
              className="w-full h-auto group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                <h4 className="font-semibold text-lg mb-1">{entry.name}</h4>
                <p className="text-sm text-white/90">{new Date(entry.date).toLocaleDateString('de-DE')}</p>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
